import React from "react";
import Link from "next/link";
import { ArrowRight, Download, Star } from "lucide-react";
import { CTASection, FAQSection, FeatureGrid, HeroSection, ContentBlock, ClientLogos, CaseStudyHighlights, LeadMagnets } from "@/components/sections";
import { HomeHeroCarousel } from "@/components/sections/home-hero-carousel";
import { ContactForm } from "@/components/contact-form";
import { IconRenderer } from "@/components/ui/icon-renderer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { resolvePublicAssetUrl, resolvePublicPageUrl } from "@/lib/api";
import { isSaasSection } from "@/components/sections/saas-section-keys";
import { SaasSectionLoader } from "@/components/sections/saas-section-loader";
import SolutionsListFallback from "@/components/sections/solutions-list-fallback";

interface DynamicSection {
  id?: string;
  sectionKey: string;
  title?: string | null;
  content: Record<string, unknown>;
  isEnabled: boolean;
}

interface DynamicPage {
  title?: string;
  description?: string | null;
  sections?: DynamicSection[];
}

const INDUSTRY_CARD_STYLES = [
  {
    gradient: "from-orange-500 via-amber-400 to-rose-400",
    accent: "bg-orange-50 text-orange-700 ring-orange-200/80",
    glow: "group-hover:shadow-[0_28px_80px_-32px_rgba(249,115,22,0.55)]",
  },
  {
    gradient: "from-sky-500 via-cyan-400 to-blue-500",
    accent: "bg-sky-50 text-sky-700 ring-sky-200/80",
    glow: "group-hover:shadow-[0_28px_80px_-32px_rgba(14,165,233,0.55)]",
  },
  {
    gradient: "from-emerald-500 via-teal-400 to-cyan-500",
    accent: "bg-emerald-50 text-emerald-700 ring-emerald-200/80",
    glow: "group-hover:shadow-[0_28px_80px_-32px_rgba(16,185,129,0.55)]",
  },
  {
    gradient: "from-violet-500 via-fuchsia-400 to-purple-500",
    accent: "bg-violet-50 text-violet-700 ring-violet-200/80",
    glow: "group-hover:shadow-[0_28px_80px_-32px_rgba(139,92,246,0.55)]",
  },
  {
    gradient: "from-rose-500 via-pink-400 to-fuchsia-500",
    accent: "bg-rose-50 text-rose-700 ring-rose-200/80",
    glow: "group-hover:shadow-[0_28px_80px_-32px_rgba(244,63,94,0.55)]",
  },
  {
    gradient: "from-indigo-500 via-blue-500 to-cyan-400",
    accent: "bg-indigo-50 text-indigo-700 ring-indigo-200/80",
    glow: "group-hover:shadow-[0_28px_80px_-32px_rgba(99,102,241,0.55)]",
  },
] as const;

function asString(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function asObjectList<T extends Record<string, unknown>>(value: unknown): T[] {
  return Array.isArray(value) ? value.filter((item): item is T => Boolean(item && typeof item === "object")) : [];
}

function asFeatureList(value: unknown) {
  return asObjectList<Record<string, unknown>>(value)
    .map((feature) => ({
      title: asString(feature.title),
      description: asString(feature.description),
    }))
    .filter((item) => item.title);
}

function asFaqList(value: unknown) {
  return asObjectList<Record<string, unknown>>(value)
    .map((faq) => ({
      question: asString(faq.question),
      answer: asString(faq.answer),
    }))
    .filter((item) => item.question && item.answer);
}

function isExternalHref(href: string) {
  return /^https?:\/\//i.test(href);
}

function resolveCardHref(item: Record<string, unknown>, sectionKey: string) {
  const explicitHref = resolvePublicPageUrl(asString(item.linkUrl) || asString(item.link) || asString(item.href));
  if (explicitHref) {
    return explicitHref;
  }

  const slug = asString(item.slug);
  if (sectionKey === "products" && slug) {
    return `/products/${slug}`;
  }

  if (sectionKey === "industries" && slug) {
    return `/industries/${slug}`;
  }

  return "#";
}

function describeLegacyListCard(item: Record<string, unknown>, sectionKey: string) {
  const raw = asString(item.description || item.tagline).trim();
  if (raw.length >= 28) {
    return raw;
  }

  const title = asString(item.title || item.name);
  const normalized = title.toLowerCase();

  if (sectionKey === "services") {
    return `${title} is built to reduce friction in your funnel and increase qualified conversions from existing traffic.`;
  }
  if (sectionKey === "products") {
    return `${title} gives teams a focused workflow to launch, measure, and improve high-impact campaigns faster.`;
  }
  if (sectionKey === "features") {
    return `${title} adds a conversion-ready interaction layer that improves engagement and visitor intent signals.`;
  }
  if (/integration|connect|sync/.test(normalized)) {
    return `${title} helps connect your stack so marketing and product teams can execute with less operational overhead.`;
  }

  return `${title} helps growth teams ship better on-site experiences with clearer outcomes and stronger conversion momentum.`;
}

function getLegacyCardTag(sectionKey: string, title: string) {
  const key = `${sectionKey} ${title}`.toLowerCase();
  if (/service|support|delivery/.test(key)) return "Service";
  if (/product|platform|suite/.test(key)) return "Product";
  if (/feature|widget|popup/.test(key)) return "Feature";
  if (/industry|vertical/.test(key)) return "Industry";
  return "Solution";
}

function CardLink({ href, className, children }: { href: string; className: string; children: React.ReactNode }) {
  if (isExternalHref(href)) {
    return <a href={href} className={className} target="_blank" rel="noreferrer">{children}</a>;
  }

  return <Link href={href} className={className}>{children}</Link>;
}

function LegacyHeroSection({ content }: { content: Record<string, unknown> }) {
  return <HomeHeroCarousel fallbackContent={content} slides={asObjectList<Record<string, unknown>>(content.slides)} autoRotateMs={typeof content.autoRotateMs === "number" ? content.autoRotateMs : 7000} />;
}

function LegacyStatsSection({ content }: { content: Record<string, unknown> }) {
  const stats = asObjectList<Record<string, unknown>>(content.stats);

  return (
    <section className="border-y border-border bg-muted/30 py-16 lg:py-20">
      <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:grid-cols-2 sm:px-6 lg:grid-cols-4 lg:px-8">
        {stats.map((stat) => {
          return (
            <div key={`${asString(stat.label)}-${asString(stat.value)}`} className="rounded-3xl border border-border bg-background/80 p-6 shadow-sm backdrop-blur-sm">
              <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${asString(stat.color, "from-sky-500 to-cyan-500")} text-white`}>
                <IconRenderer iconKey={asString(stat.iconKey)} className="h-5 w-5" emojiClassName="text-2xl leading-none" />
              </div>
              <div className="text-4xl font-bold text-foreground">{asString(stat.value)}</div>
              <div className="mt-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">{asString(stat.label)}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function LegacyCardsSection({ content, sectionKey }: { content: Record<string, unknown>; sectionKey: string }) {
  const eyebrow = asString(content.eyebrow);
  const heading = asString(content.heading);
  const description = asString(content.description);
  const cards = asObjectList<Record<string, unknown>>(content.cards || content.items);

  return (
    <section className="py-14 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 w-full">
          {eyebrow ? <span className="text-sm font-semibold uppercase tracking-wider text-primary">{eyebrow}</span> : null}
          <h2 className="mt-3 max-w-5xl text-balance text-3xl font-bold text-foreground lg:text-5xl">{heading}</h2>
          {description ? <p className="mt-4 max-w-3xl text-lg text-muted-foreground">{description}</p> : null}
        </div>

        <div className={sectionKey === "industries" ? "grid gap-6 md:grid-cols-2 xl:grid-cols-3" : "grid gap-6 md:grid-cols-2 xl:grid-cols-4"} style={{ contentVisibility: "auto", containIntrinsicSize: "1100px" }}>
          {cards.map((item) => {
            const href = resolveCardHref(item, sectionKey);
            const title = asString(item.title || item.name);
            const description = describeLegacyListCard(item, sectionKey);
            const cardTag = getLegacyCardTag(sectionKey, title);
            return (
              <CardLink key={`${asString(item.title || item.name)}-${href}`} href={href} className="group block h-full">
                <Card className="relative h-full overflow-hidden border-border/70 bg-background/95 transition-colors duration-300 hover:border-primary/30">
                  <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${asString(item.gradient, "from-sky-500 via-cyan-500 to-blue-500")}`} />
                  <CardContent className="p-6">
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <div className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${asString(item.gradient, "from-sky-500 to-cyan-500")} text-white shadow-md`}>
                        <IconRenderer iconKey={asString(item.iconKey)} className="h-5 w-5" emojiClassName="text-2xl leading-none" />
                      </div>
                      <span className="rounded-full border border-primary/15 bg-primary/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
                        {cardTag}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground group-hover:text-primary">{title}</h3>
                    <p className="mt-2 text-sm leading-7 text-muted-foreground">{description}</p>
                    <div className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary transition-all duration-300 group-hover:gap-3">
                      Learn More <ArrowRight className="h-4 w-4" />
                    </div>
                  </CardContent>
                </Card>
              </CardLink>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function IndustriesCardsSection({ content }: { content: Record<string, unknown> }) {
  const eyebrow = asString(content.eyebrow);
  const heading = asString(content.heading);
  const description = asString(content.description);
  const cards = asObjectList<Record<string, unknown>>(content.items || content.cards);

  return (
    <section className="relative overflow-hidden py-14 lg:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(56,189,248,0.14),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(249,115,22,0.12),transparent_28%)]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          {eyebrow ? <span className="text-sm font-semibold uppercase tracking-wider text-primary">{eyebrow}</span> : null}
          <h2 className="mt-3 text-3xl font-bold text-foreground lg:text-5xl">{heading}</h2>
          {description ? <p className="mt-4 text-lg text-muted-foreground">{description}</p> : null}
        </div>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3" style={{ contentVisibility: "auto", containIntrinsicSize: "1100px" }}>
          {cards.map((item, index) => {
            const href = resolveCardHref(item, "industries");
            const palette = INDUSTRY_CARD_STYLES[index % INDUSTRY_CARD_STYLES.length];
            const title = asString(item.title || item.name);

            return (
              <CardLink key={`${title}-${href}`} href={href} className="group block h-full">
                <Card className="relative h-full overflow-hidden border-border/70 bg-background/95 transition-colors duration-300 hover:border-primary/30">
                  <div className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${palette.gradient}`} />
                  <div className="absolute right-0 top-0 h-28 w-28 rounded-full bg-white/50 blur-3xl transition-transform duration-500 group-hover:scale-125" />
                  <CardContent className="relative flex h-full flex-col p-6">
                    <div className={`mb-5 flex h-14 w-14 items-center justify-center rounded-2xl ring-1 ${palette.accent}`}>
                      <IconRenderer iconKey={asString(item.iconKey)} className="h-5 w-5" emojiClassName="text-2xl leading-none" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground transition-colors group-hover:text-primary">{title}</h3>
                    <p className="mt-3 text-sm leading-7 text-muted-foreground">{asString(item.description)}</p>
                    <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                      Learn More
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </div>
                  </CardContent>
                </Card>
              </CardLink>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ProductShowcaseSection({ content }: { content: Record<string, unknown> }) {
  const eyebrow = asString(content.eyebrow);
  const heading = asString(content.heading);
  const description = asString(content.description);
  const items = asObjectList<Record<string, unknown>>(content.items);
  const cta = content.cta as Record<string, unknown> | undefined;

  if (items.length === 0) {
    return null;
  }

  return (
    <section className="relative overflow-hidden py-14 lg:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.14),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.14),transparent_24%)]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            {eyebrow ? <span className="text-sm font-semibold uppercase tracking-[0.24em] text-primary">{eyebrow}</span> : null}
            <h2 className="mt-3 text-3xl font-bold text-foreground lg:text-5xl">{heading}</h2>
            {description ? <p className="mt-4 text-lg text-muted-foreground">{description}</p> : null}
          </div>
          {asString(cta?.url) ? (
            <Link href={resolvePublicPageUrl(asString(cta?.url, "/products"))}>
              <Button variant="outline" size="lg">{asString(cta?.text, "Explore Products")}</Button>
            </Link>
          ) : null}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {items.map((item) => {
            const href = resolveCardHref(item, "products");
            return (
              <CardLink key={`${asString(item.name)}-${href}`} href={href} className="group block h-full">
                <Card className="h-full overflow-hidden border-border/70 bg-background/95 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl">
                  <CardContent className="flex h-full flex-col p-0">
                    <div className={`relative min-h-[180px] overflow-hidden bg-gradient-to-br ${asString(item.gradient, "from-sky-500 to-cyan-500")} p-6 text-white`}>
                      <div className="absolute right-4 top-4 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em]">{asString(item.category, "Product")}</div>
                      <div className="flex h-full flex-col justify-between gap-6">
                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/30">
                          {asString(item.image) ? <img src={asString(item.image)} alt={asString(item.title || item.name)} className="h-10 w-10 object-contain" /> : <IconRenderer iconKey={asString(item.iconKey)} className="h-6 w-6" emojiClassName="text-3xl leading-none" />}
                        </div>
                        <div>
                          <h3 className="text-2xl font-semibold">{asString(item.title || item.name)}</h3>
                          <p className="mt-3 max-w-sm text-sm leading-7 text-white/80">{asString(item.description || item.tagline)}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <div className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-primary">
                        View Product
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CardLink>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function StrategicPartnersSection({ content }: { content: Record<string, unknown> }) {
  const eyebrow = asString(content.eyebrow);
  const heading = asString(content.heading);
  const description = asString(content.description);
  const items = asObjectList<Record<string, unknown>>(content.items);
  const cta = content.cta as Record<string, unknown> | undefined;

  if (items.length === 0) {
    return null;
  }

  return (
    <section className="relative overflow-hidden py-14 lg:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.12),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(8,145,178,0.12),transparent_26%)] dark:bg-[radial-gradient(circle_at_top_left,rgba(96,165,250,0.16),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(45,212,191,0.14),transparent_26%)]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            {eyebrow ? <span className="text-sm font-semibold uppercase tracking-[0.24em] text-primary">{eyebrow}</span> : null}
            <h2 className="mt-3 text-3xl font-bold text-foreground lg:text-5xl">{heading}</h2>
            {description ? <p className="mt-4 text-lg text-muted-foreground">{description}</p> : null}
          </div>
          {asString(cta?.url) ? (
            <Link href={resolvePublicPageUrl(asString(cta?.url, "/contact-us"))}>
              <Button variant="outline" size="lg">{asString(cta?.text, "Become a Partner")}</Button>
            </Link>
          ) : null}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((item) => {
            const href = asString(item.href, "#");
            return (
              <CardLink key={`${asString(item.name)}-${href}`} href={href} className="group block h-full">
                <Card className="h-full border-border/70 bg-card/85 text-card-foreground shadow-[0_20px_45px_-34px_rgba(15,23,42,0.28)] backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-[0_22px_55px_-30px_rgba(37,99,235,0.22)] dark:bg-card/70">
                  <CardContent className="flex h-full flex-col gap-4 p-5">
                    <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl bg-muted ring-1 ring-border/70">
                      {asString(item.logo) ? <img src={asString(item.logo)} alt={asString(item.logoAlt || item.name)} className="h-9 w-9 object-contain opacity-90 transition-opacity duration-300 group-hover:opacity-100" /> : <span className="text-lg font-semibold text-primary">{asString(item.name).slice(0, 2).toUpperCase()}</span>}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">{asString(item.name)}</h3>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">{asString(item.description, "Strategic ecosystem collaborator supporting faster delivery and stronger technical outcomes.")}</p>
                    </div>
                    <div className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-primary">
                      Visit Partner
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </div>
                  </CardContent>
                </Card>
              </CardLink>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function LegacyTestimonialsSection({ content }: { content: Record<string, unknown> }) {
  const eyebrow = asString(content.eyebrow);
  const heading = asString(content.heading);
  const description = asString(content.description);
  const items = asObjectList<Record<string, unknown>>(content.items);

  if (!items.length) return null;

  return (
    <section className="gradient-bg py-14 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          {eyebrow ? <span className="text-sm font-semibold uppercase tracking-wider text-primary">{eyebrow}</span> : null}
          <h2 className="mt-3 text-3xl font-bold text-foreground lg:text-5xl">{heading}</h2>
          {description ? <p className="mt-4 text-lg text-muted-foreground">{description}</p> : null}
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {items.map((item) => (
            <Card key={`${asString(item.name)}-${asString(item.role)}`} className="h-full border-border bg-card/80 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="mb-4 flex items-center gap-1 text-amber-400">
                  {Array.from({ length: Number(item.rating) || 5 }).map((_, index) => (
                    <Star key={`${asString(item.name)}-${index}`} className="h-4 w-4 fill-current" />
                  ))}
                </div>
                <p className="text-sm leading-7 text-muted-foreground">&ldquo;{asString(item.content)}&rdquo;</p>
                <div className="mt-5">
                  <p className="font-semibold text-foreground">{asString(item.name)}</p>
                  <p className="text-sm text-muted-foreground">{asString(item.role || [item.designation, item.company].filter(Boolean).join(", "))}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

export function DynamicPageRenderer({ page }: { page: DynamicPage }) {
  const sections = (page.sections || []).filter((section) => section.isEnabled);
  const hasListing = Array.isArray(page.sections)
    ? (page.sections as DynamicSection[]).some((s) => ["cards", "legacy-cards", "services", "products", "industries", "features"].includes(s.sectionKey))
    : false;

  const shouldInjectSolutionsList = ((page.title || "").toString().toLowerCase() === "solutions") && !hasListing;

  return (
    <>
      {sections.map((section) => {
        const heading = asString(section.content.heading, section.title || page.title || "");
        const description = asString(section.content.description, page.description || "");
        if (isSaasSection(section.sectionKey)) {
          if (section.sectionKey === "saas_hero" && shouldInjectSolutionsList) {
            return (
              <React.Fragment key={section.id || section.sectionKey}>
                <SaasSectionLoader sectionKey={section.sectionKey} content={section.content} />
                <SolutionsListFallback />
              </React.Fragment>
            );
          }

          return (
            <SaasSectionLoader
              key={section.id || section.sectionKey}
              sectionKey={section.sectionKey}
              content={section.content}
            />
          );
        }

        if (section.sectionKey === "hero") {
          return <LegacyHeroSection key={section.id || section.sectionKey} content={section.content} />;
        }

        if (section.sectionKey === "client_logos" || section.sectionKey === "client-logos") {
          return (
            <ClientLogos
              key={section.id || section.sectionKey}
              eyebrow={asString(section.content.eyebrow)}
              heading={asString(section.content.heading, "Trusted by Leading Companies")}
              description={asString(section.content.description)}
              showNames={Boolean(section.content.showNames ?? true)}
              clients={asObjectList<{ name: string; industry?: string; logo?: string | null; logoAlt?: string; href?: string; description?: string | null }>(section.content.clients)}
            />
          );
        }

        if (section.sectionKey === "stats") {
          return <LegacyStatsSection key={section.id || section.sectionKey} content={section.content} />;
        }

        if (section.sectionKey === "industries") {
          return <IndustriesCardsSection key={section.id || section.sectionKey} content={section.content} />;
        }

        if (section.sectionKey === "services") {
          return <LegacyCardsSection key={section.id || section.sectionKey} content={section.content} sectionKey={section.sectionKey} />;
        }

        if (section.sectionKey === "products") {
          return <ProductShowcaseSection key={section.id || section.sectionKey} content={section.content} />;
        }

        if (section.sectionKey === "strategic_partners") {
          return <StrategicPartnersSection key={section.id || section.sectionKey} content={section.content} />;
        }

        if (section.sectionKey === "case_studies" || section.sectionKey === "case-studies") {
          return (
            <CaseStudyHighlights
              key={section.id || section.sectionKey}
              eyebrow={asString(section.content.eyebrow, "Proven Results")}
              heading={asString(section.content.heading, "Real Results for Real Businesses")}
              description={asString(section.content.description)}
              ctaHref={resolvePublicPageUrl(asString((section.content.cta as Record<string, unknown>)?.url, "/resources/case-studies"))}
              ctaLabel={asString((section.content.cta as Record<string, unknown>)?.text, "View All Case Studies")}
              highlights={asObjectList<{ client: string; title: string; metric: string; metricLabel: string; description: string; tags: string[] }>(section.content.items)}
            />
          );
        }

        if (section.sectionKey === "testimonials") {
          return <LegacyTestimonialsSection key={section.id || section.sectionKey} content={section.content} />;
        }

        if (section.sectionKey === "lead_magnets" || section.sectionKey === "lead-magnets") {
          return (
            <LeadMagnets
              key={section.id || section.sectionKey}
              eyebrow={asString(section.content.eyebrow, "Free Resources")}
              heading={asString(section.content.heading, "Guides & Templates to Get You Started")}
              description={asString(section.content.description)}
              resources={asObjectList<{ iconKey?: string; title: string; description: string; format: string; gradient: string; href?: string; category?: string; fileSize?: number | null }>(section.content.items).map((item) => ({
                ...item,
                href: resolvePublicAssetUrl(asString(item.href)),
              }))}
            />
          );
        }

        if (section.sectionKey === "cta") {
          return (
            <section key={section.id || section.sectionKey} className="py-14 lg:py-28">
              <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
                <div className="overflow-hidden rounded-[32px] bg-slate-950 px-8 py-14 text-center text-white shadow-2xl shadow-slate-900/20">
                  <h2 className="text-3xl font-bold lg:text-5xl">{heading}</h2>
                  <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">{description}</p>
                  <div className="mt-8 flex flex-wrap justify-center gap-4">
                    <Link href={resolvePublicPageUrl(asString((section.content.primaryCta as Record<string, unknown>)?.url, "/contact-us"))}>
                      <Button variant="gradient" size="lg" className="cta-aurora-button bg-[length:220%_220%] from-cyan-500 via-blue-600 to-fuchsia-600">
                        {asString((section.content.primaryCta as Record<string, unknown>)?.text, "Get Free Consultation")}
                      </Button>
                    </Link>
                    <Link href={resolvePublicPageUrl(asString((section.content.secondaryCta as Record<string, unknown>)?.url, "/about-us"))}>
                      <Button variant="outline" size="lg" className="cta-secondary-button">
                        {asString((section.content.secondaryCta as Record<string, unknown>)?.text, "Learn About Us")}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          );
        }

        if (section.sectionKey === "features") {
          return (
            <FeatureGrid
              key={section.id || section.sectionKey}
              title={section.title || heading}
              subtitle={description}
              features={asFeatureList(section.content.items || section.content.features)}
            />
          );
        }

        if (section.sectionKey === "faq" || section.sectionKey === "faqs") {
          return (
            <FAQSection
              key={section.id || section.sectionKey}
              title={section.title || heading}
              faqs={asFaqList(section.content.items || section.content.faqs)}
            />
          );
        }

        if (section.sectionKey === "text" || section.sectionKey === "content" || section.sectionKey === "custom") {
          return (
            <section key={section.id || section.sectionKey} className="py-16">
              <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                {section.title ? <h2 className="mb-6 text-3xl font-bold text-foreground">{section.title}</h2> : null}
                <ContentBlock html={asString(section.content.html, description)} />
              </div>
            </section>
          );
        }

        if (section.sectionKey === "contact_form" || section.sectionKey === "contact-form") {
          return (
            <section key={section.id || section.sectionKey} id="contact-form" className="py-16 lg:py-20">
              <div className="mx-auto grid max-w-6xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
                <div className="space-y-4">
                  <span className="text-sm font-semibold uppercase tracking-wider text-primary">Contact</span>
                  <h2 className="text-3xl font-bold text-foreground lg:text-5xl">{heading}</h2>
                  <p className="text-lg text-muted-foreground">{description}</p>
                </div>
                <ContactForm />
              </div>
            </section>
          );
        }

        if (section.sectionKey === "generic-hero") {
          return (
            <HeroSection
              key={section.id || section.sectionKey}
              title={heading}
              description={description}
              typography={section.content?.typography as Record<string, unknown>}
              primaryCta={asString(section.content.buttonText) ? { label: asString(section.content.buttonText), href: resolvePublicPageUrl(asString(section.content.buttonLink, "/contact")) } : undefined}
              secondaryCta={asString(section.content.secondaryButtonText) ? { label: asString(section.content.secondaryButtonText), href: resolvePublicPageUrl(asString(section.content.secondaryButtonLink, "/")) } : undefined}
            />
          );
        }

        if (section.sectionKey === "generic-cta") {
          return (
            <CTASection
              key={section.id || section.sectionKey}
              title={section.title || heading}
              description={description}
              primaryCta={{ label: asString(section.content.buttonText, "Get Started"), href: resolvePublicPageUrl(asString(section.content.buttonLink, "/contact")) }}
              secondaryCta={asString(section.content.secondaryButtonText) ? { label: asString(section.content.secondaryButtonText), href: resolvePublicPageUrl(asString(section.content.secondaryButtonLink, "/")) } : undefined}
              variant="gradient"
            />
          );
        }

        return null;
      })}
    </>
  );
}
